import React, {useCallback, useEffect, useState} from 'react';
import {Col, Container, Row} from 'reactstrap';
import {Location} from '@reach/router';
import queryString from 'query-string';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {faCheck} from '@fortawesome/pro-light-svg-icons/faCheck';
import {faTimes} from '@fortawesome/pro-light-svg-icons/faTimes';
import {faArrowDown} from '@fortawesome/pro-regular-svg-icons';
import classNames from 'classnames';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';

import {usePhaseState} from 'funnel-schedule/phase-context';
import {useUserState} from 'contexts/user';
import useBalanceText from 'utils/useBalanceText';

import PageMeta from 'components/PageMeta';
import Section, {SectionTriangle, SectionTriangleSpeakerCollapse} from 'components/Elements/Section';
import Header from 'components/Elements/Header';
import Video from 'components/Video';
import {ListIcon} from 'components/Elements/IconList';
import TimerSection from 'components/Landing/TimerSection';
import MasterclassInfographicForm from 'components/MasterclassInfographicForm';
import QuizMasterclassOptin from 'components/Forms/QuizMasterclassOptin';
import OptinModal, {OptinModalTrigger} from 'components/OptinModal';
import ModalExitLanding from 'components/Modals/ModalExitLanding';
import Footer from 'components/Elements/Footer';

import {questions} from 'data/questions.json';
import affids from 'data/affids.json';
import CountdownTimer from 'components/Elements/CountdownTimer';
import SectionCL from 'components/ComponentLibrary/SectionCL';
import IconListCL from 'components/ComponentLibrary/IconListCL/IconListCL';
import {TestimonialBoxNew} from 'components/Testimonial';

export default function Index(props) {
  return (
    <Location>
      {({location, navigate}) => <MasterClassIndex location={location} navigate={navigate} {...props} />}
    </Location>
  );
}
function MasterClassIndex({
  location = {},
  noShare = false,
  noExitIntent = false,
  linkLogo = false,
  slug = 'masterclass',
  navigate,
  variant = 'default',
  ...props
}) {
  const {email} = useUserState();
  const qs = typeof window !== 'undefined' ? queryString.parse(window.location.search) : {};
  const {fromInfographic} = qs.fromInfographic ? qs : location.state || {};
  const {fromQuiz, answerSet, score, answers} = qs.fromQuiz ? qs : location.state || {};
  const navigateWatch = useCallback(() => navigate('/masterclass/watch/'), []);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const pathName = window.location.pathname;
      const pathRegex = new RegExp(`\/${slug}\/`, 'g');
      if (!pathName.match(pathRegex)) {
        navigate(`/${slug}/` + location.search);
      }
    }
  }, []);

  const {
    phases: {
      default: defaultSchedule = {
        phase: 'before',
        launchStart: '',
        launchEnd: '',
        launchTitle: ''
      },
      special: specialSchedule = {}
    }
  } = usePhaseState();

  const launchEnd = (
    specialSchedule.phase === 'during' && specialSchedule.launchEnd
      ? new Date(specialSchedule.launchEnd)
      : new Date(defaultSchedule.launchEnd)
  ).getTime();

  let partnerName;
  if (typeof window !== 'undefined') {
    const qsAffiliate = queryString.parse(window.location.search);
    if (qsAffiliate.affid && affids[qsAffiliate.affid]) {
      ({partnerName} = affids[qsAffiliate.affid]);
    }
  }

  const phase = partnerName && specialSchedule.phase ? specialSchedule.phase : defaultSchedule.phase;

  function Logo({className = null, src = 'https://cdn.foodrevolution.org/hbc/hbc-bbm-icon-logo.svg'}) {
    return (
      <img src={src} alt="break breakthroughs masterclass logo" style={{maxHeight: '90px'}} className={className} />
    );
  }

  function Timer({className = undefined, timerStyle = 'dark', timerClass = undefined}) {
    return (
      <>
        {defaultSchedule.phase === 'during' || (partnerName && specialSchedule.phase === 'during') ? (
          <div className={classNames(`section-countdown`, className)}>
            <h5 className="timer-heading mt-4 mb-2">
              {partnerName && specialSchedule.launchTitle ? specialSchedule.launchTitle : defaultSchedule.launchTitle}{' '}
              ends in:
            </h5>
            <CountdownTimer time={launchEnd} className={classNames(`countdown-${timerStyle}`, timerClass)} />
          </div>
        ) : null}
      </>
    );
  }

  const [pricePointControl, setpricePointControl] = useState(props.pricePointControl || 'control');
  useEffect(() => {
    subscribeToCampaign((variantIndex) => {
      switch (variantIndex) {
        case 1:
          setpricePointControl('v1');
          break;
      }
    }, 1038);
  }, []);
  const priceEntry = {control: '247', v1: '197'}[pricePointControl];

  const ctaButton =
    (fromInfographic && email) || (fromQuiz && email) ? (
      <>
        {fromInfographic && <MasterclassInfographicForm fromInfographic={fromInfographic} entry={priceEntry} />}
        {fromQuiz && <QuizMasterclassOptin fromQuiz={fromQuiz} entry={priceEntry} />}
      </>
    ) : (
      <OptinModalTrigger btnText="Reserve My Spot" />
    );

  function Question({question, name, options, value, answer, theirAnswer = null, ...props}) {
    return (
      <div id={value} className="quiz-question-wrap">
        <h5 className="section-heading">
          <b>{question}</b>
        </h5>
        {theirAnswer ? (
          <div className="quiz-answer-incorrect text-red mb-2">
            <span className="quiz-answer-icon">
              <FontAwesomeIcon icon={faTimes} />
            </span>{' '}
            Your Answer: {options[theirAnswer]}
          </div>
        ) : null}
        <div className="quiz-answer-correct text-green">
          <span className="quiz-answer-icon">
            <FontAwesomeIcon icon={faCheck} />
          </span>{' '}
          Correct Answer: {options[answer]}
        </div>
      </div>
    );
  }
  function QuestionMap() {
    return (
      <div className="quiz-answers-wrap text-left">
        {questions.map((question, i) =>
          !answerSet[i] ? <Question {...question} key={i} theirAnswer={answers[i]} /> : null
        )}
      </div>
    );
  }

  //useBalanceText();
  return (
    <div className="page page-nosocial d-flex flex-column">
      <PageMeta
        title="Join the FREE Brain Breakthroughs Masterclass."
        description="Discover the key to thriving brain health at any age with FRN CEO, Ocean Robbins, and leading neurologists Dean and Ayesha Sherzai, MD in this free Masterclass."
      />
      <div className="page-content">
        {partnerName && (
          <>
            <Section id="partner-banner" color="dark-gray" innerClass="text-center text-white py-4" angle="none">
              <Container>
                <Row>
                  <Col>
                    <p className="mb-0">
                      {partnerName} invites you to join the <i>Brain Breakthroughs Masterclass</i>
                    </p>
                    {specialSchedule.isLastChance && specialSchedule.lastChanceText ? (
                      <p className="mt-3 mb-0">{specialSchedule.lastChanceText}</p>
                    ) : null}
                  </Col>
                </Row>
              </Container>
            </Section>
          </>
        )}
        {fromInfographic && (
          <section className="section-infographic-info background-light-gray py-4">
            <Container>
              <Row className="d-flex align-items-center">
                <Col xs="3" md="2" className="text-center">
                  <img
                    className="img-infographic img-fluid"
                    src="https://cdn.foodrevolution.org/hbc/hbc-infographic-3d.png"
                  />
                </Col>
                <Col xs="9" md="10">
                  Thanks for taking an interest in your brain health! Your poster of the{' '}
                  <i className="text-800">Top 10 Foods To Eat & Avoid For Brain Health</i> should be in your email inbox
                  in a few minutes.
                </Col>
              </Row>
            </Container>
          </section>
        )}
        <Header bgColor="white" textColor="black" adLockup={variant === 'ad' ? true : false} />
        {fromQuiz && (
          <>
            {score < 100 ? (
              <SectionCL
                id="section-quiz-info"
                className="section-angle-first"
                innerClass="py-5"
                color="light-gray"
                angle="none"
              >
                <Container>
                  <Row className="d-flex align-items-center">
                    <Col className="text-center">
                      <h4 className="section-heading text-400 mb-3">Thank you for taking this brain health quiz.</h4>
                      <h2 className="mt-0 mb-3">You scored: {score}%</h2>
                      <h3 className="mt-0">
                        See below for what you missed — plus the right answers! (You got all the rest right!)
                      </h3>

                      <Row className="d-flex justify-content-center">
                        <Col lg="10">
                          <QuestionMap />
                        </Col>
                      </Row>

                      {score <= 69 ? (
                        <>
                          <h3 className="mb-0">
                            Want to learn more about how to keep your brain healthy? We have exciting news for you!
                          </h3>
                        </>
                      ) : null}

                      {score >= 70 && score <= 99 ? (
                        <>
                          <h3 className="mb-2">Not too bad! You know quite a bit about brain health.</h3>
                          <h3 className="m-0">Now take it to the next level.</h3>
                        </>
                      ) : null}

                      <Row className="d-flex justify-content-center mt-5">
                        <Col sm="10" md="8" lg="6" xl="5">
                          <div className="arrows d-flex justify-content-between">
                            <FontAwesomeIcon className="text-icon" icon={faArrowDown} />
                            <FontAwesomeIcon className="text-icon" icon={faArrowDown} />
                            <FontAwesomeIcon className="text-icon" icon={faArrowDown} />
                          </div>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Container>
              </SectionCL>
            ) : (
              <SectionCL id="section-quiz-info" color="light-gray" innerClass="text-center py-4" angle="none">
                <Container>
                  <Row>
                    <Col>
                      <h4 className="section-heading text-400 mb-3">Thank you for taking this brain health quiz.</h4>
                      <h2 className="m-0">You scored: {score}%</h2>
                    </Col>
                  </Row>
                </Container>
              </SectionCL>
            )}
          </>
        )}
        <SectionCL
          id="hbcv1-header-image"
          color="gray"
          imgUrl="https://cdn.foodrevolution.org/hbc/images/1434534146-lg.jpg"
          innerWrapClass="flex-grow-1"
          type="triangle"
          order="first"
          angle="none"
        />
        <SectionCL id="hbcv1-header-optin" color="white" type="triangle" angle="both">
          <Container>
            <Row>
              <Col>
                <div className="optin-box">
                  <div className="optin-box-wrap background-white p-4 p-lg-5 rounded box-shadow-black-50">
                    <p className="text-h3 text-center text-lg-left text-400">
                      In This <b>90-Minute Masterclass,</b> <br className="d-none d-md-block d-xl-none" />
                      You’ll Discover:
                    </p>
                    <Row className="mx-0">
                      <Col lg="7" xl="8">
                        <div className="d-flex justify-content-center justify-content-lg-start">
                          <IconListCL iconColor="black">
                            <>Breakthroughs in neuroscience that can help switch off dementia genes</>
                            <>Foods for improved sleep, more energy, and better memory</>
                            <>Rewiring your brain for healthy habits that stick</>
                            <>The dangerous brain health myths</>
                            <>The Brain Docs' journey to the Alzheimer’s solution</>
                          </IconListCL>
                        </div>
                        <div className="text-center text-lg-left">{ctaButton}</div>
                      </Col>
                      <Col lg="5" xl="4" className="d-none d-lg-flex">
                        <div className="optin-box-image">
                          <img src="https://cdn.foodrevolution.org/hbc/images/sherzai-isolated-20250603001.png" />
                        </div>
                      </Col>
                    </Row>
                  </div>
                  {variant != 'ad' && <Timer className="text-center mt-4" />}
                </div>
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL id="hbcv1-testimoinals" color="light-gray" type="triangle">
          <Container>
            <Row>
              <Col>
                <TestimonialBoxNew className="mb-4" bgColor="white" id="dan-buettner" />
                <TestimonialBoxNew className="mb-4" bgColor="white" id="david-katz" />
                <TestimonialBoxNew bgColor="white" id="ocean-robbins" />
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL
          id="hbcv1-letter"
          color="dark-gray"
          imgUrl="https://cdn.foodrevolution.org/global/images/1284690585-lg.jpg"
          type="triangle"
          angle="none"
        >
          <Container>
            <Row>
              <Col>
                <div className="background-white box-shadow-black p-4 p-lg-5 rounded">
                  <h3 className="section-heading">Hi, I’m Dr. Dean Sherzai</h3>
                  <p>
                    “How do you move a knight?” When my grandfather, a master chess player, asked me that question...I
                    knew something was wrong.
                  </p>
                  <p>
                    It marked the moment our whole family started walking the heartbreaking road of cognitive decline
                    with him.
                  </p>
                  <p>Today, there are over 50 million people facing exactly what my grandfather did:</p>
                  <ul>
                    <li>The insecurity of not being able to do things that used to come naturally</li>
                    <li>The embarrassment of relying fully on caregivers</li>
                    <li>The overwhelming pity in place of respect</li>
                    <li>The confusion, anxiety, and sleeplessness that come with memory loss</li>
                  </ul>
                  <p>As a neurologist, the lack of solutions for mental decline has been a constant agony for me.</p>
                  <p>
                    So 15 years ago, I left behind my lucrative career in pharmaceutical research, determined to find
                    answers.
                  </p>
                  <p>
                    Since then, my wife Ayesha (also a neurologist) and I have studied thousands of people with
                    brain-related issues…
                  </p>
                  <p>And we couldn’t be happier with what our results have shown…</p>
                  <p>
                    <b>It IS possible to reduce the risk of permanent memory loss — dramatically!</b>
                  </p>
                  <p>We’re sharing the results of our study in our Brain Breakthroughs Masterclass.</p>
                  <p>
                    Permanent memory loss is the most expensive chronic disease on the planet. And most people have come
                    to think it’s just an inevitable consequence of growing older.
                  </p>
                  <p>But in our research, we’ve learned that the opposite is true.</p>
                  <p>
                    Our brains are meant to THRIVE. Brains can actually REGENERATE. They can get STRONGER (not weaker)
                    with each passing year!
                  </p>
                  <p>We want to destroy the myth that dementia is inevitable with aging.</p>
                  <p>
                    And to really show you the truth: <b>We hold the power to nurture a healthy brain in our hands.</b>
                  </p>
                  <p>Register now for the free Brain Breakthroughs Masterclass.</p>
                  <div className="text-center">{ctaButton}</div>
                </div>
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL id="hbcv1-instructors" color="black" className="text-center text-white" angle="both" type="triangle">
          <Container>
            <Row>
              <Col>
                <h3 className="section-only-heading">Meet Your Instructors</h3>
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL
          id="hbcv1-instructors-bio"
          color="white"
          imgUrl="https://cdn.foodrevolution.org/global/speakers/frn-guest_expert-portrait-square-Dean_Ayesha_Sherzai_2024.jpg"
          imgPosition="right"
          type="triangle"
        >
          <Container>
            <Row>
              <Col>
                <h2 className="section-heading text-purple mb-2">Dean Sherzai, MD, PhD & Ayesha Sherzai, MD</h2>
                <h4 className="mt-0 text-400">
                  Cutting-edge neurologists, brain science researchers, and best-selling authors.
                </h4>
                <p>
                  Dean Sherzai, MD, and Ayesha Sherzai, MD, are the authors of the books The Alzheimer’s Solution and
                  The 30-Day Alzheimer's Solution: The Definitive Food and Lifestyle Guide to Preventing Cognitive
                  Decline.
                </p>
                <p>
                  They are co-directors of the Healthy Minds Initiative and creators of The NEURO Plan, a customizable
                  brain health program that utilizes technology and neurological research to empower users to improve
                  their brain health.
                </p>
                <p>
                  They host the popular Brain Health Revolution Podcast and have been featured in The Sunday Times, O
                  Magazine, The Wall Street Journal, and The Huffington Post and on ABC News, CNN, and BBC Good Food.
                </p>
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL id="hbcv1-host" color="black" className="text-center text-white" type="triangle">
          <Container>
            <Row>
              <Col>
                <h3 className="section-only-heading">Meet Your Host</h3>
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL
          id="hbcv1-host-bio"
          color="white"
          imgUrl="https://cdn.foodrevolution.org/global/team/frn-guest_expert-portrait-square-ocean_robbins.jpg"
          imgPosition="right"
          type="triangle"
        >
          <Container>
            <Row>
              <Col>
                <h2 className="section-heading text-purple mb-2">Ocean Robbins</h2>
                <h4 className="mt-0 text-400">A food revolution leader and inspiration to millions.</h4>
                <p>
                  Ocean Robbins, co-founder and CEO of the million-member Food Revolution Network, is a TEDx speaker,
                  best-selling author, husband, father, gardener, and dancer. Born in a log cabin and raised on
                  home-grown food, his work is rooted in his family’s health-conscious legacy. From founding the
                  nonprofit YES! at age 16 to leading a global food revolution movement, Ocean is on a mission to
                  transform our food system, drive down the chronic disease epidemic, and mobilize people worldwide to
                  be part of the solution for a healthier planet and population. His books include{' '}
                  <i>31-Day Food Revolution</i> and <i>Powered By Plants.</i>
                </p>
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL
          id="hbcv1-final-cta"
          color="purple"
          className="text-center text-shadow text-white"
          type="triangle"
          angle="none"
          order="last"
        >
          <Container>
            <Row>
              <Col>
                <h2 className="section-heading mb-2">
                  <i>Don’t Miss Your Chance!</i>
                </h2>
                <h3 className="m-0">For a limited time watch the</h3>
                <Logo src="https://cdn.foodrevolution.org/hbc/hbc-bbm-icon-logo-white.svg" className="my-4" />
                <h5 className="mt-0">It’s completely FREE.</h5>
                <div>{ctaButton}</div>
                {variant != 'ad' && <Timer className="mt-4" timerStyle="light" />}
              </Col>
            </Row>
          </Container>
        </SectionCL>
      </div>

      <div className="footer-content mt-auto">
        <Footer adVariant={variant === 'ad' ? true : false} />
      </div>
      <OptinModal entry={priceEntry} />
      {!noExitIntent && <ModalExitLanding />}
    </div>
  );
}
